/**
 * UI language. English is the source text; `t('English text')` returns the German when the
 * language is German and a translation exists, otherwise the English. Missing translations
 * therefore never break the screen. `{name}` placeholders are filled from the second argument.
 *
 * The default comes from a stored preference, else from the tenant's locale, else English.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from './auth'
import { getLang, LANG_KEY, setCurrentLang, type Lang } from './lang'
import { DE } from './de'

interface I18n {
  lang: Lang
  setLang: (l: Lang) => void
  t: (en: string, vars?: Record<string, string | number>) => string
}

const Ctx = createContext<I18n | null>(null)

function fill(s: string, vars?: Record<string, string | number>) {
  if (!vars) return s
  return s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m))
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const { tenant } = useAuth()
  const [lang, setLangState] = useState<Lang>(() => {
    try {
      const stored = localStorage.getItem(LANG_KEY)
      if (stored === 'de' || stored === 'en') return stored
    } catch {
      /* no storage */
    }
    return getLang()
  })
  // follow the tenant's locale until the user picks a language
  useEffect(() => {
    try {
      if (localStorage.getItem(LANG_KEY)) return
    } catch {
      /* no storage */
    }
    if (tenant?.locale) setLangState(tenant.locale.toLowerCase().startsWith('de') ? 'de' : 'en')
  }, [tenant?.locale])
  useEffect(() => {
    setCurrentLang(lang)
    document.documentElement.lang = lang
  }, [lang])
  const setLang = useCallback((l: Lang) => {
    setLangState(l)
    try {
      localStorage.setItem(LANG_KEY, l)
    } catch {
      /* no storage */
    }
  }, [])
  const t = useCallback((en: string, vars?: Record<string, string | number>) => fill(lang === 'de' ? (DE[en] ?? en) : en, vars), [lang])
  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useT(): I18n {
  const v = useContext(Ctx)
  if (!v) throw new Error('useT outside I18nProvider')
  return v
}

/** Pick the German name of a catalogue entity when the UI is German and one exists. */
export function localName(lang: Lang, name: string, nameDe?: string | null): string {
  return lang === 'de' && nameDe ? nameDe : name
}
